/**
 * The visitor's choice on daevio.com's cookie banner. Necessary storage (sign-in, this choice) needs no consent;
 * measurement (daevio's own script recording its visitors) runs only after "Accept". The choice is kept six
 * months, then asked again (Garante, guidelines of 10 June 2021); a new version of the banner asks again too.
 */

export const CONSENT_STORAGE_KEY = 'daevio_cookie_choice';
/** Raise when what the banner asks changes: everyone is asked again. */
export const CONSENT_VERSION = 1;
const SIX_MONTHS_MS = 182 * 24 * 60 * 60 * 1000;

export interface CookieChoice {
  version: number;
  measurement: boolean;
  /** ISO date of the choice. */
  at: string;
}

type Store = Pick<Storage, 'getItem' | 'setItem'>;

function store(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The stored choice, or null when there is none, it is from an older banner, or older than six months. */
export function readChoice(now = Date.now(), s: Store | null = store()): CookieChoice | null {
  try {
    const raw = s?.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Partial<CookieChoice>;
    if (c.version !== CONSENT_VERSION || typeof c.measurement !== 'boolean' || typeof c.at !== 'string') return null;
    const at = Date.parse(c.at);
    if (!Number.isFinite(at) || now - at > SIX_MONTHS_MS) return null;
    return { version: c.version, measurement: c.measurement, at: c.at };
  } catch {
    return null;
  }
}

export function saveChoice(measurement: boolean, now = Date.now(), s: Store | null = store()): CookieChoice {
  const choice: CookieChoice = { version: CONSENT_VERSION, measurement, at: new Date(now).toISOString() };
  try {
    s?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choice));
  } catch {
    // Private windows: the banner comes back next time; the choice still applies to this page.
  }
  return choice;
}

/** Tells daevio's own recorder (loaded with `requireConsent`) whether it may record. */
export function applyChoice(choice: Pick<CookieChoice, 'measurement'>): void {
  const daevio = (window as unknown as { daevio?: (...args: unknown[]) => void }).daevio;
  if (typeof daevio === 'function') daevio('consent', choice.measurement);
}

/** Whether this build records daevio.com's own visitors (and so shows the banner). */
export function ownRecorderOn(): boolean {
  return typeof __OWN_RECORDER__ !== 'undefined' && __OWN_RECORDER__;
}

/** The footer's "Cookie settings" asks the banner to open again. */
export const OPEN_COOKIE_SETTINGS = 'daevio:cookie-settings';

export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS));
}
